/**
 * Spelling Werkwoorden (SWW) Support Registry
 *
 * Support content (hints, explanations, visuals) for SWW families.
 *
 * Module: SWW (Spelling Werkwoorden)
 * Families: 24
 * Coverage: 100% (migrated: 2026-03-25)
 *
 * Created: 2026-03-25
 * Status: ✅ Content migrated from familySupportRegistry.ts (Step 4)
 */

import { FamilySupportEntry } from '../familySupportRegistry'

/**
 * SWW Support Definitions
 *
 * Each entry provides:
 * - defaultExplanation: Student-facing "Zo werkt het" text
 * - hints: Progressive hint steps (title + content)
 * - requiresHints: Whether hints are conceptually needed
 * - requiresVisual: Whether visual aid is conceptually needed
 * - defaultVisual: Optional default visual aid reference
 */
export const SWW_SUPPORT: Record<string, FamilySupportEntry> = {
  'SWW-TT-STAM': {
    familyId: 'SWW-TT-STAM',
    defaultExplanation:
      'De stam van een werkwoord vind je door het hele werkwoord te nemen en -en eraf te halen. Let op: soms moet je de klinker verdubbelen (lopen → loop) of een medeklinker weglaten (rennen → ren).',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'De stam = het hele werkwoord min -en.\nlopen → loop\nmaken → maak\nvinden → vind\nspelen → speel',
      },
      {
        title: 'Let op!',
        content:
          'Soms moet je de klinker verdubbelen:\nlopen - en = lop → loop (open lettergreep)\nspelen - en = spel → speel',
      },
    ],
  },
  'SWW-TT-INV-JIJ': {
    familyId: 'SWW-TT-INV-JIJ',
    defaultExplanation:
      'Bij inversie (omkering) staat "jij" achter het werkwoord. Dan valt de -t weg: "Jij loopt" → "Loop jij?" Let op: dit geldt alleen voor jij/je, niet voor hij/zij.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          'Jij staat ACHTER het werkwoord → alleen stam (geen t!)\n"Loop jij naar school?"\n"Werk je vandaag?"\n"Vind jij dat ook?"',
      },
      {
        title: 'Vergelijk',
        content:
          'Jij loopt (jij ervoor → +t)\nLoop jij? (jij erachter → geen t)',
      },
    ],
  },
  'SWW-VT-STERK': {
    familyId: 'SWW-VT-STERK',
    defaultExplanation:
      'Sterke werkwoorden veranderen van klank in de verleden tijd: lopen → liep, schrijven → schreef. Ze krijgen GEEN -te of -de. Deze vormen moet je uit je hoofd leren.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Wat zijn sterke werkwoorden?',
        content:
          'Bij sterke werkwoorden verandert de klinker:\nlopen → liep\nzingen → zong\nhelpen → hielp\nschrijven → schreef',
      },
      {
        title: 'Geen -te of -de!',
        content:
          'Sterke werkwoorden krijgen GEEN -te of -de.\nNIET: loopte, zingde\nWEL: liep, zong',
      },
    ],
  },
  'SWW-VT-ZWAK-KOF': {
    familyId: 'SWW-VT-ZWAK-KOF',
    defaultExplanation:
      "Gebruik 't kofschip om te bepalen of de verleden tijd op -te(n) of -de(n) eindigt. Eindigt de stam op t, k, f, s, ch of p? Dan +te(n). Anders +de(n).",
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: "'t Kofschip — de letters",
        content:
          "De letters van 't Kofschip: T, K, O, F, S, CH, I, P\n\nEindigt de stam op één van deze letters?\n→ Verleden tijd: stam + te (enkelvoud) of + ten (meervoud)\n\nVoorbeelden:\nwerk → werkte/werkten (K)\nfiets → fietste/fietsten (S)\nblaf → blafte (F)",
      },
      {
        title: 'Niet in kofschip → -de/-den',
        content:
          'Eindigt de stam NIET op een kofschip-letter?\n→ stam + de (enkelvoud) of + den (meervoud)\n\nVoorbeelden:\nleer → leerde (R)\nspeel → speelden (L)\nlev → leefde (V → F wissel!)',
      },
      {
        title: 'Uitzonderingen & sterke werkwoorden',
        content:
          "Pas op voor:\n• Sterke werkwoorden: lopen → liep (NIET loopte!)\n  Klankverandering = sterk = geen kofschip\n• V/F-wissel: verven → verfde (klinkt beter dan verfte)\n• Signaalwoorden: 'gisteren', 'vorige week' = verleden tijd\n• Geen hulpwerkwoord? Dan geen voltooid deelwoord!",
      },
    ],
  },
  'SWW-VD-KOF': {
    familyId: 'SWW-VD-KOF',
    defaultExplanation:
      'Het voltooid deelwoord: ge + stam + t (bij kofschip) of ge + stam + d (geen kofschip). Voorbeeld: werken → gewerkt, leven → geleefd.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          "Stam eindigt op 't kofschip letter?\n→ ge + stam + t\n\nVoorbeelden:\nwerk → gewerkt (K)\ndans → gedanst (S)\npost → gepost (T, al een t dus geen extra)",
      },
    ],
  },
  'SWW-TT-DSTAM': {
    familyId: 'SWW-TT-DSTAM',
    defaultExplanation:
      'Als de stam eindigt op -d, krijg je bij hij/zij: d + t = dt. Voorbeeld: hij vindt (stam = vind). Bij "ik" schrijf je alleen de stam: ik vind.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Regel',
        content:
          'Eindigt de stam op een d? Dan schrijf je bij hij/zij: d + t = dt.\nhij vindt (stam = vind)\nhij wordt (stam = word)\nhij verzendt (stam = verzend)',
      },
      {
        title: 'Ik-vorm: geen t!',
        content:
          'Bij "ik" alleen de stam:\nik vind (niet: ik vindt)\nik word (niet: ik wordt)',
      },
    ],
  },
  'SWW-VT-VZFS': {
    familyId: 'SWW-VT-VZFS',
    defaultExplanation:
      'Let op de v→f en z→s wissel! Bij "verven" is de stam "verf" (f zit in kofschip → verfde). Kijk altijd naar het hele werkwoord, niet alleen de stam.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'V/F-wissel',
        content:
          'Stam is verf, maar het werkwoord is verVen.\nKijk naar de V (niet in kofschip) → verfde\n\nRegel: kijk altijd naar het hele werkwoord!',
      },
      {
        title: 'Z/S-wissel',
        content:
          'Stam is verhuis, maar het werkwoord is verhuiZen.\nZ niet in kofschip → verhuisde',
      },
    ],
  },
  'SWW-VT-DUBBEL': {
    familyId: 'SWW-VT-DUBBEL',
    defaultExplanation:
      'Als de stam al eindigt op -d of -t, krijg je dubbele letters: redden → redde, antwoorden → antwoordde, wachten → wachtte.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Dubbele letters',
        content:
          'Stam op d + de = dubbel d: red → redde\nStam op t + te = dubbel t: wacht → wachtte',
      },
    ],
  },
  'SWW-BN-VD-KORT': {
    familyId: 'SWW-BN-VD-KORT',
    defaultExplanation:
      'Bij een bijvoeglijk naamwoord van een voltooid deelwoord met lange klank laat je een klinker weg: vereren → de vereerde (niet verereerde).',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Regel',
        content:
          'Als het voltooid deelwoord als bijvoeglijk naamwoord wordt gebruikt,\nmaak je het zo kort mogelijk + e:\n\nverbrand → verbrande (auto)\ngered → geredde (jongen, korte klinker → dubbel d)\ngeverfd → geverfde (muur)',
      },
    ],
  },
  'SWW-TT-INV-TRICK': {
    familyId: 'SWW-TT-INV-TRICK',
    defaultExplanation:
      'Pas op: inversie geldt alleen bij jij/je. Bij "Wanneer komt hij?" blijft de -t staan, want "hij" is het onderwerp, niet "jij".',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          'Jij staat ACHTER het werkwoord → alleen stam (geen t!)\n"Loop jij naar school?"\n"Werk je vandaag?"\n"Vind jij dat ook?"',
      },
      {
        title: 'Let op!',
        content:
          'Dit geldt ALLEEN voor "jij" of "je".\nBij "hij" blijft de t staan: "LoopT hij?"',
      },
    ],
  },
  'SWW-BN-VD-DUBBEL': {
    familyId: 'SWW-BN-VD-DUBBEL',
    defaultExplanation:
      'Bij een korte klinker verdubbel je de medeklinker: gered → de geredde (niet gerede). Bij een lange klinker niet: gepoot → de gepote.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Let op korte klinkers',
        content:
          'Bij een korte klinker verdubbel je de medeklinker:\ngered → geredde (niet: gerede)\ngepot → gepotte (niet: gepote)\n\nBij een lange klinker niet:\ngepoot → gepote (niet: gepotte)\nbesteed → bestede (niet: besteedde)',
      },
    ],
  },
  'SWW-VD-HOMOFOON': {
    familyId: 'SWW-VD-HOMOFOON',
    defaultExplanation:
      'Word of wordt? Gebruik de "smurfen-test": vervang het werkwoord door "smurfen". Klinkt "smurf" goed? Dan geen -t. Klinkt "smurft" goed? Dan wel -t.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Leiden vs. Lijden',
        content:
          'Leiden (ei) = de weg wijzen, aanvoeren\nLijden (ij) = pijn hebben, ondergaan\n\nHij leidt de vergadering.\nHij lijdt aan hoofdpijn.',
      },
      {
        title: 'Word vs. Wordt',
        content:
          'Ik word (stam, geen t)\nHij wordt (stam + t)\nWord jij? (inversie, geen t)',
      },
    ],
  },
  'SWW-LEEN-ENG-STAM': {
    familyId: 'SWW-LEEN-ENG-STAM',
    defaultExplanation:
      'Bij Engelse leenwoorden bepaal je de stam op dezelfde manier: het hele werkwoord min -en. Let op de klank, niet de spelling: racen → race.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Stappenplan',
        content:
          'De stam = het hele werkwoord min -en.\nlopen → loop\nmaken → maak\nvinden → vind\nspelen → speel',
      },
    ],
  },
  'SWW-LEEN-ENG-KOF': {
    familyId: 'SWW-LEEN-ENG-KOF',
    defaultExplanation:
      "Bij Engelse leenwoorden pas je 't kofschip toe op de KLANK van de stam. Race (klank s, zit in kofschip) → racete. Download (klank d, niet in kofschip) → downloadde.",
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        content:
          "De letters van 't Kofschip: T, K, F, S, CH, P\n\nEindigt de stam op één van deze letters?\n→ Verleden tijd: stam + te (enkelvoud) of + ten (meervoud)\n\nVoorbeelden:\nwerk → werkte/werkten (K)\nfiets → fietste/fietsten (S)\nblaf → blafte (F)",
      },
    ],
  },
  'SWW-TT-ZIJN-HEBBEN': {
    familyId: 'SWW-TT-ZIJN-HEBBEN',
    defaultExplanation:
      'Zijn en hebben zijn onregelmatige werkwoorden. Je moet de vormen uit je hoofd leren: ik ben, jij bent, hij is, wij zijn. Ik heb, jij hebt, hij heeft, wij hebben.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Zijn — tegenwoordige tijd',
        content:
          'ik ben\njij bent / ben jij? (inversie: geen t)\nhij/zij is\nwij/jullie/zij zijn',
      },
      {
        title: 'Hebben — tegenwoordige tijd',
        content:
          'ik heb\njij hebt / heb jij? (inversie: geen t)\nhij/zij heeft\nwij/jullie/zij hebben',
      },
    ],
  },
  'SWW-TT-MEERVOUD': {
    familyId: 'SWW-TT-MEERVOUD',
    defaultExplanation:
      'Bij meervoud (wij, jullie, zij) gebruik je altijd het hele werkwoord: wij lopen, jullie fietsen, zij werken. Geen -t toevoegen!',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Regel',
        content:
          'Meervoud = het hele werkwoord:\nwij lopen (niet: wij loopt)\njullie fietsen (niet: jullie fietst)\nzij werken (niet: zij werkt)',
      },
      {
        title: 'Let op!',
        content:
          'Alleen bij hij/zij enkelvoud voeg je -t toe.\nBij wij/jullie/zij meervoud NOOIT een -t.',
      },
    ],
  },
  'SWW-TT-GEBIEDENDE': {
    familyId: 'SWW-TT-GEBIEDENDE',
    defaultExplanation:
      'De gebiedende wijs is een bevel of verzoek. Je gebruikt alleen de stam: "Loop!", "Kom hier!", "Ga zitten!"',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Regel',
        content:
          'Gebiedende wijs = alleen de stam:\nLopen → Loop!\nKomen → Kom!\nZitten → Zit!\n\nGeen -t, geen -en, alleen de stam.',
      },
      {
        title: 'Beleefd',
        content:
          'Beleefde gebiedende wijs met u:\n"Gaat u zitten" (stam + t)\n"Komt u binnen"',
      },
    ],
  },
  'SWW-VT-ZIJN-HEBBEN': {
    familyId: 'SWW-VT-ZIJN-HEBBEN',
    defaultExplanation:
      'Zijn en hebben in de verleden tijd: ik was, jij was, hij was, wij waren. Ik had, jij had, hij had, wij hadden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Zijn — verleden tijd',
        content: 'ik was\njij was\nhij/zij was\nwij/jullie/zij waren',
      },
      {
        title: 'Hebben — verleden tijd',
        content: 'ik had\njij had\nhij/zij had\nwij/jullie/zij hadden',
      },
    ],
  },
  'SWW-VT-ONREGELMATIG': {
    familyId: 'SWW-VT-ONREGELMATIG',
    defaultExplanation:
      'Onregelmatige werkwoorden volgen geen vast patroon. De verleden tijd moet je uit je hoofd leren: doen → deed, gaan → ging, staan → stond.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Veelvoorkomende onregelmatige werkwoorden',
        content:
          'doen → deed/deden\ngaan → ging/gingen\nstaan → stond/stonden\nzien → zag/zagen\nkomen → kwam/kwamen',
      },
      {
        title: 'Tip',
        content:
          'Onregelmatige werkwoorden herken je doordat de klinker verandert EN ze niet in het kofschip-patroon passen.\nLeer ze per groep!',
      },
    ],
  },
  'SWW-VT-ZULLEN-KUNNEN': {
    familyId: 'SWW-VT-ZULLEN-KUNNEN',
    defaultExplanation:
      'Hulpwerkwoorden in de verleden tijd: zullen → zou/zouden, kunnen → kon/konden, willen → wilde/wilden, mogen → mocht/mochten.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Hulpwerkwoorden verleden tijd',
        content:
          'zullen → zou (ek) / zouden (mv)\nkunnen → kon / konden\nwillen → wilde / wilden (of: wou/wouden)\nmogen → mocht / mochten',
      },
      {
        title: 'Gebruik',
        content:
          'Zou + infinitief = beleefd verzoek of voorwaarde:\n"Zou je me willen helpen?"\n"Als ik kon, zou ik komen."',
      },
    ],
  },
  'SWW-VD-STERK': {
    familyId: 'SWW-VD-STERK',
    defaultExplanation:
      'Het voltooid deelwoord van sterke werkwoorden eindigt op -en: gelopen, geschreven, gezwommen. De klinker verandert vaak.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Sterke VD',
        content:
          'Sterke werkwoorden krijgen ge- ... -en:\nlopen → gelopen\nschrijven → geschreven\nzwemmen → gezwommen\nrijden → gereden',
      },
      {
        title: 'Let op de klankverandering',
        content:
          'De klinker verandert vaak:\nzingen → gezongen (i → o)\nhelpen → geholpen (e → o)\nbinden → gebonden (i → o)',
      },
    ],
  },
  'SWW-VD-ZONDER-GE': {
    familyId: 'SWW-VD-ZONDER-GE',
    defaultExplanation:
      'Werkwoorden die beginnen met be-, ver-, ont-, her-, ge- of er- krijgen GEEN ge- in het voltooid deelwoord: betalen → betaald, vertellen → verteld.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Geen ge- bij:',
        content:
          'be-: betalen → betaald\nver-: vertellen → verteld\nont-: ontdekken → ontdekt\nher-: herhalen → herhaald\nge-: gebruiken → gebruikt\ner-: erkennen → erkend',
      },
      {
        title: 'Tip',
        content:
          'Klinkt het raar met ge-? Dan hoort het er niet:\n"gebetaald" klinkt fout → betaald ✓\n"geverteld" klinkt fout → verteld ✓',
      },
    ],
  },
  'SWW-VD-HULPWW': {
    familyId: 'SWW-VD-HULPWW',
    defaultExplanation:
      'Bij het voltooid deelwoord gebruik je een hulpwerkwoord: hebben of zijn. "Ik heb gelopen" of "Ik ben gevallen". Beweging/verandering → zijn.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Hebben of zijn?',
        content:
          'Zijn: bij verandering van plaats of toestand\nik ben gevallen, ik ben gegroeid, ik ben vertrokken\n\nHebben: bij de rest\nik heb gelopen, ik heb gegeten, ik heb gewerkt',
      },
      {
        title: 'Ezelsbruggetje',
        content:
          'Kun je "ergens naartoe" of "iets worden"?\n→ zijn\nAnders → hebben',
      },
    ],
  },
  'SWW-LEEN-FRANS': {
    familyId: 'SWW-LEEN-FRANS',
    defaultExplanation:
      'Franse leenwoorden als werkwoord: de stam bepaal je op dezelfde manier (-en eraf). Let op de v→f wissel: verven → verf.',
    requiresHints: true,
    requiresVisual: false,
    hints: [
      {
        title: 'Franse werkwoorden',
        content:
          'Behandel ze als gewone Nederlandse werkwoorden:\nreserveren → reserveerde (r, niet in kofschip → -de)\nfourneren → fourneerde\nriskeren → riskeerde',
      },
      {
        title: 'V/F-wissel',
        content:
          'Let op bij verven:\nStam = verf (f in kofschip)\nMaar "verfte" klinkt raar → verfde\nDit is een uitzondering!',
      },
    ],
  },
}

/**
 * Module Statistics
 * Auto-calculated from support entries
 */
export const SWW_SUPPORT_STATS = {
  moduleCode: 'SWW',
  moduleName: 'Spelling Werkwoorden',
  totalFamilies: Object.keys(SWW_SUPPORT).length,
  withHints: Object.values(SWW_SUPPORT).filter((e) => e.hints?.length).length,
  withVisuals: Object.values(SWW_SUPPORT).filter((e) => e.defaultVisual).length,
  withExplanations: Object.values(SWW_SUPPORT).filter(
    (e) => e.defaultExplanation,
  ).length,
  requiresHintsCount: Object.values(SWW_SUPPORT).filter((e) => e.requiresHints)
    .length,
  requiresVisualCount: Object.values(SWW_SUPPORT).filter(
    (e) => e.requiresVisual,
  ).length,
}
